import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/cn.js'
import { money } from '../../lib/format.js'
import { useTRPC } from '../../lib/trpc.js'
import type { CatalogItem } from '../catalog/CatalogItemPopup.js'
import { Icon } from '../Icon.js'
import { AutoTextarea } from '../inputs.js'

/**
 * Portalled to `body`, so it has to clear the job card and anything stacked
 * on it: `useOverlay` puts those at 50 plus ten a level.
 */
const LIST_Z = 1000
const GAP = 4
const EDGE = 8

interface Placement {
  left: number
  width: number
  top?: number
  bottom?: number
}

/**
 * A billing line's Item box: the words on the line, and a search of our
 * catalog as they are typed.
 *
 * The text is always the description, so a line nobody picks for is a
 * one-off line exactly as before. Picking fills the line from the item; the
 * last row makes a new item for someone who can edit the catalog. Nothing is
 * active until an arrow key moves there, because Enter in a description is
 * also a new line.
 *
 * The list is portalled at fixed coordinates, as `Popover` is: the line table
 * sits in a panel that clips its overflow.
 */
export function CatalogLineField({
  index,
  value,
  clientId,
  searchable,
  canCreate,
  onChange,
  onPick,
  onCreate,
}: {
  /** Zero-based; the labels count from one. */
  index: number
  value: string
  clientId: string | null
  /** Off without CATALOG_VIEW, which leaves a plain text box. */
  searchable: boolean
  canCreate: boolean
  onChange: (value: string) => void
  onPick: (item: CatalogItem) => void
  onCreate: (name: string) => void
}) {
  const trpc = useTRPC()
  const listId = useId()
  const anchor = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [placement, setPlacement] = useState<Placement | null>(null)

  const term = value.trim().slice(0, 100)
  const label = `Line ${index + 1} description`

  const results = useQuery({
    ...trpc.catalog.searchMaterials.queryOptions({ q: term, limit: 8, ...(clientId ? { clientId } : {}) }),
    enabled: searchable && open,
    placeholderData: keepPreviousData,
  })

  const items = results.data ?? []
  const count = items.length + (canCreate ? 1 : 0)

  useEffect(() => setActive(-1), [term])

  const position = useCallback(() => {
    const node = anchor.current
    if (!node) return
    const rect = node.getBoundingClientRect()
    const width = Math.min(Math.max(rect.width, 320), window.innerWidth - EDGE * 2)
    const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - width - EDGE))
    const below = window.innerHeight - rect.bottom

    setPlacement(
      below < 240 && rect.top > below
        ? { left, width, bottom: window.innerHeight - rect.top + GAP }
        : { left, width, top: rect.bottom + GAP },
    )
  }, [])

  useLayoutEffect(() => {
    if (open) position()
  }, [open, position, value])

  useEffect(() => {
    if (!open) return
    window.addEventListener('resize', position)
    window.addEventListener('scroll', position, true)
    return () => {
      window.removeEventListener('resize', position)
      window.removeEventListener('scroll', position, true)
    }
  }, [open, position])

  const close = () => {
    setOpen(false)
    setActive(-1)
  }

  const choose = (at: number) => {
    const item = items[at]
    if (item) onPick(item)
    else if (canCreate) onCreate(term)
    close()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!open) setOpen(true)
      else setActive((at) => Math.min(count - 1, at + 1))
    } else if (event.key === 'ArrowUp' && open) {
      event.preventDefault()
      setActive((at) => Math.max(-1, at - 1))
    } else if (event.key === 'Enter' && open && active >= 0) {
      event.preventDefault()
      choose(active)
    } else if (event.key === 'Escape' && open) {
      // Otherwise Escape also closes the job card the line is in.
      event.preventDefault()
      event.stopPropagation()
      close()
    } else if (event.key === 'Tab') {
      close()
    }
  }

  if (!searchable) {
    return (
      <AutoTextarea
        label={label}
        value={value}
        placeholder="Description"
        onChange={onChange}
        className="min-w-0 border-0 font-medium text-ink-900 placeholder:text-ink-400"
      />
    )
  }

  const optionId = (at: number) => `${listId}-${at}`
  const expanded = open && placement !== null

  return (
    <div ref={anchor} className="flex min-w-0 items-start gap-1">
      <AutoTextarea
        label={label}
        value={value}
        placeholder="Search the catalog, or type a line"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={expanded ? listId : undefined}
        aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
        onFocus={() => {
          if (!value.trim()) setOpen(true)
        }}
        onBlur={close}
        onKeyDown={onKeyDown}
        onChange={(next) => {
          onChange(next)
          setOpen(true)
        }}
        className="min-w-0 flex-1 border-0 font-medium text-ink-900 placeholder:text-ink-400"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={`Choose line ${index + 1} from the catalog`}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          anchor.current?.querySelector('textarea')?.focus()
          if (open) close()
          else setOpen(true)
        }}
        className="flex size-5 shrink-0 items-center justify-center rounded text-ink-400 hover:bg-ink-100 hover:text-ink-800"
      >
        <Icon name="chevronDown" className={cn('size-3 transition-transform', open && 'rotate-180')} strokeWidth={2.25} />
      </button>

      {expanded
        ? createPortal(
            <div
              id={listId}
              role="listbox"
              aria-label="Catalog items"
              style={{ ...placement, zIndex: LIST_Z }}
              className="fixed max-h-72 overflow-y-auto rounded-lg bg-white p-1 text-[13px] shadow-lg ring-1 ring-ink-200"
              // Keeps focus in the line, so choosing does not blur and close first.
              onMouseDown={(event) => event.preventDefault()}
            >
              {items.map((item, at) => (
                <div
                  key={item.id}
                  id={optionId(at)}
                  role="option"
                  aria-selected={active === at}
                  onMouseEnter={() => setActive(at)}
                  onClick={() => choose(at)}
                  className={cn(
                    'flex cursor-pointer items-baseline gap-2 rounded-md px-2 py-1.5',
                    active === at && 'bg-ink-100',
                  )}
                >
                  <span className="w-20 shrink-0 truncate font-mono text-[11px] text-ink-500">{item.code ?? ''}</span>
                  <span className="min-w-0 flex-1 truncate text-ink-900">{item.name}</span>
                  <span className="shrink-0 tabular-nums text-ink-600">{money(item.unitPrice)}</span>
                </div>
              ))}

              {items.length === 0 ? (
                <p className="px-2 py-1.5 text-xs text-ink-500">
                  {results.isError
                    ? 'The catalog could not be searched. What you type stays a one-off line.'
                    : results.isFetching
                      ? 'Searching…'
                      : term
                        ? 'Nothing in the catalog matches. It stays a one-off line.'
                        : 'Nothing in our catalog yet.'}
                </p>
              ) : null}

              {canCreate ? (
                <div
                  id={optionId(items.length)}
                  role="option"
                  aria-selected={active === items.length}
                  onMouseEnter={() => setActive(items.length)}
                  onClick={() => choose(items.length)}
                  className={cn(
                    'mt-1 flex cursor-pointer items-center gap-2 rounded-md border-t border-ink-100 px-2 py-1.5 font-medium text-brand-700',
                    active === items.length && 'bg-ink-100',
                  )}
                >
                  <Icon name="plus" className="size-3.5" />
                  <span className="min-w-0 truncate">
                    {term ? `New catalog item “${term}”` : 'New catalog item'}
                  </span>
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
